import { Autocomplete, Box, styled, Typography } from '@mui/material'
import React, { forwardRef, memo, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Virtuoso } from 'react-virtuoso'
import CustomTextField from '../textfield'
import RequiredLabelComponent from '../required-label-component'

// styled component
const ErrorText = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  marginBottom: '0.25rem',
  lineHeight: '1.154',
  color: 'var(--error-main)',
  marginTop: '4px'
}))

const VirtualisedList = memo(
  forwardRef((props, ref) => {
    const { children, ...rest } = props

    const items = useMemo(() => {
      return children ? React.Children.toArray(children) : []
    }, [children])

    return (
      <Box ref={ref}>
        <Box {...rest}>
          <Virtuoso
            style={{ height: 300 }}
            totalCount={items?.length || 0}
            itemContent={(index) => {
              return React.cloneElement(items[index], {
                dataindex: index
              })
            }}
          />
        </Box>
      </Box>
    )
  })
)

const CustomAutocomplete = memo(
  ({ needVirtualization, options, renderInput, error, placeholder = '', ...rest }) => {
    return (
      <Autocomplete
        options={options || []}
        renderInput={(params) =>
          renderInput ? (
            renderInput(params)
          ) : (
            <CustomTextField
              error={error}
              placeholder={placeholder}
              {...params}
            />
          )
        }
        {...(needVirtualization ? { ListboxComponent: VirtualisedList } : {})}
        {...rest}
      />
    )
  }
)

const VirtualizedAutocomplete = ({
  name,
  control,
  label,
  renderInput,
  options = [],
  error = false,
  helperText = '',
  required = false,
  tooltipText = '',
  needVirtualization = true,
  fieldClassName = '',
  autoCompleteProps = {}
}) => {
  return control ? (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur } }) => (
        <Box className={fieldClassName}>
          {label && (
            <RequiredLabelComponent
              label={label}
              error={error}
              required={required}
              tooltipText={tooltipText}
            />
          )}
          <CustomAutocomplete
            value={value}
            onBlur={onBlur}
            options={options}
            onChange={(_, values) => {
              onChange(values)
            }}
            renderInput={renderInput}
            needVirtualization={needVirtualization}
            error={error}
            {...autoCompleteProps}
          />
          {error && helperText && <ErrorText>{helperText}</ErrorText>}
        </Box>
      )}
    />
  ) : (
    <Box>
      {label && (
        <RequiredLabelComponent
          label={label}
          error={error}
          required={required}
          tooltipText={tooltipText}
        />
      )}
      <CustomAutocomplete
        options={options}
        renderInput={renderInput}
        needVirtualization={needVirtualization}
        {...autoCompleteProps}
      />
      {error && helperText && <ErrorText>{helperText}</ErrorText>}
    </Box>
  )
}

export default memo(VirtualizedAutocomplete)
